import { BaseGameController } from '@/components/game/implementations/_base/_domain/controller/game-controller';
import { inject, injectable } from 'inversify';
import { IConfigurationService } from '@/app-modules/config';
import { IServerServiceFactory } from '@/app-modules/server-service';
import { CatchUpData, ResetData } from '@/components/game/_domain/server';
import { HamsterRaceCatchUpData, HamsterRaceResetData } from '@/components/game/implementations/uk/hamster-race/_domain/models';
import { IGame, GameState, IGameRepresentation, GameIdentity, IGameConfiguration } from '@/components/game/_domain/models';
import { HamsterRacePrizeWinner } from '@/components/game/implementations/uk/hamster-race/_domain/models';
import { HamsterRaceInfoData, IHamsterStats } from '@/components/game/_domain/server/hamster-race-info/hamster-race-info-data';
import { IHamsterRaceTicketSetConfigurationViewModel } from '@/app-modules/config/models/hamster-race-ticket-set-response-view-model';
import { IWinnersTrackerService, ITrackedWinner } from '@/components/game/_domain/modules/winners-tracker-service';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { PrizeWinnerData } from '@/components/game/_domain/server/prize-winner/prize-winner-data';
import { IErrorService } from '@/app-modules/error';

const NUMBER_OF_HAMSTERS = 8;


// TODO: TECH DEBT: Move IHamsterRaceGameRepresentation interface to seperate file (subject to wider team discussion around directory structure)
interface IHamsterRaceGameRepresentation extends IGameRepresentation {
  hamsterSet?: number;
  hamsterInFirstPlace?: IHamster;
  hamsters?: IHamster[];
  lastEightWinnersNames?: string[];
  hamsterStats?: IHamsterStats[];
  hamsterWinners?: IHamsterRaceWinner[];
}

interface IHamsterRaceWinner {
  username: string;
  prizeInPence: number;
}

class HamsterRaceWinner implements IHamsterRaceWinner {
  public username: string;
  public prizeInPence: number;

  constructor(userName: string, prizeInPence: number) {
    this.username = userName;
    this.prizeInPence = prizeInPence;
  }
}

// TODO: TECH DEBT: Move IHamster interface to seperate file (subject to wider team discussion around directory structure)
interface IHamster
{
  hamsterNumber: number;
  hamsterPosition: number;
}

// TODO: TECH DEBT: Move HamsterCollection class to seperate file (subject to wider team discussion around directory structure)
class HamsterCollection {
  private hamsters: IHamster[];

  public initialise() {
    this.hamsters = this.initialiseHamsters();
  }

  public getAll(): IHamster[] {
    return this.hamsters.slice(0);
  }

  public advanceHamsterPosition(hamsterNumber: number) {
    const hamster = this.findHamsterByHamsterNumber(hamsterNumber);
    if (hamster) {
      hamster.hamsterPosition++;
    }
  }

  public findHamsterByHamsterNumber(hamsterNumber: number): IHamster | null {
    const hamsters = this.hamsters.filter((hamster) => hamster.hamsterNumber === hamsterNumber);
    if (hamsters.length === 1) {
      return hamsters[0];
    }
    return null;
  }

  public getHamsterInFirstPlace(): IHamster {
    const hamsters = this.hamsters.slice(0).sort((hamsterA, hamsterB) => {
      if (hamsterB.hamsterPosition > hamsterA.hamsterPosition) {
        return 1;
      }

      if (hamsterB.hamsterPosition < hamsterA.hamsterPosition) {
        return -1;
      }

      return 0;
    });
    return hamsters[0];
  }

  private initialiseHamsters(): IHamster[] {
    const hamsters: IHamster[] = [];
    for (let hamsterNumber = 1; hamsterNumber <= NUMBER_OF_HAMSTERS; hamsterNumber++) {
      hamsters.push({
        hamsterNumber,
        hamsterPosition: 0
      });
    }
    return hamsters;
  }
}

// TODO: TECH DEBT: To pass first and second place in single argument.
type HamsterRaceGameLoopCallback = (hamsterInFirstPlace: IHamster, hamsters: IHamster[]) => void;

// TODO: TECH DEBT: Move HamsterRaceGameLoopHandler class to seperate file (subject to wider team discussion around directory structure)
// TODO: TECH DEBT: Consider incorporating HamsterRaceGameLoopHandler into an IGameServerService implementation for Hamsterace
class HamsterRaceGameLoopHandler
{
  private readonly GAME_STEP_INTERVAL_MILLISECONDS = 100;
  private readonly hamsters = new HamsterCollection();
  private gameSequence: number[];
  private gamePosition: number = -1;
  private gameIntervalHandle: number;

  public initialise(gameSequence: string) {
    this.stopGameLoop();
    this.hamsters.initialise();
    this.gameSequence = this.initialiseGameSequence(gameSequence);
    this.gamePosition = -1;
  }

  public startGameLoop(gameLoopCallback: HamsterRaceGameLoopCallback, initialStepsTaken: number = 0) {
    this.stopGameLoop();
    this.advanceGamePosition(initialStepsTaken);
    this.gameIntervalHandle = window.setInterval(() => { this.handleGameLoop(gameLoopCallback); }, this.GAME_STEP_INTERVAL_MILLISECONDS);
  }

  public stopGameLoop() {
    if (this.gameIntervalHandle) {
      window.clearInterval(this.gameIntervalHandle);
      this.gameIntervalHandle = null;
    }
  }

  private handleGameLoop(gameLoopCallback: HamsterRaceGameLoopCallback) {
    if (this.isGameComplete()) {
      this.stopGameLoop();
      return;
    }

    this.advanceGamePosition();
    const hamsterInFirstPlace = this.hamsters.getHamsterInFirstPlace();
    const allHamsters = this.hamsters.getAll();
    gameLoopCallback(hamsterInFirstPlace, allHamsters);
  }

  public isGameComplete() {
    return this.gamePosition >= this.gameSequence.length - 1;
  }

  private advanceGamePosition(stepsTaken: number = 1) {
    for (let stepNumber = 0; stepNumber < stepsTaken; stepNumber++) {
      const newGamePosition = this.gamePosition + 1;
      if (newGamePosition < this.gameSequence.length) {
        this.gamePosition = newGamePosition;
        const hamsterNumber = this.getHamsterNumberAtCurrentGamePosition();
        this.hamsters.advanceHamsterPosition(hamsterNumber);
      }
      else {
        break;
      }
    }
  }

  private getHamsterNumberAtCurrentGamePosition(): number | null {
    if (this.gamePosition >= 0 && this.gamePosition < this.gameSequence.length) {
      return this.gameSequence[this.gamePosition];
    }
    return null;
  }

  private initialiseGameSequence(gameSequence: string): number[] {
    return gameSequence.split('').map((hamsterNumber) => Number(hamsterNumber));
  }
}

// TODO: TECH DEBT: Move class to seperate file (subject to wider team discussion around directory structure)
class HamsterRaceGame implements IGame {
  public constructor(public readonly gameIdentity: GameIdentity, public readonly gameConfiguration: IGameConfiguration) {
    this.connectionDebugInfo = 'Disconnected';
    this.state = GameState.InPlay;
  }

  public connectionDebugInfo: string;
  public state: GameState;
  public representation?: IHamsterRaceGameRepresentation;
  public gameLoopHandler?: HamsterRaceGameLoopHandler;
  public currentHamsterSet: number;

  public updateRepresentation(newGameRepresentation: Partial<IHamsterRaceGameRepresentation>): void {
    this.representation = Object.assign({}, this.representation, newGameRepresentation || {});
  }
}

@injectable()
class HamsterRaceGameController extends BaseGameController {
  protected game: HamsterRaceGame;
  private prizeWinnersPayload: HamsterRacePrizeWinner;

  public constructor(
      @inject(SERVICE_IDENTIFIERS.IConfigurationService) configurationService: IConfigurationService,
      @inject(SERVICE_IDENTIFIERS.IServerServiceFactory) serverServiceFactory: IServerServiceFactory,
      @inject(SERVICE_IDENTIFIERS.IWinnersTrackerService) winnersTrackerService: IWinnersTrackerService,
      @inject(SERVICE_IDENTIFIERS.IErrorService) errorService: IErrorService
  ) {
    super(configurationService, serverServiceFactory, winnersTrackerService, errorService);
  }

  public initialise(gameIdentity: GameIdentity) {
    this.game = this.getInitialHamsterRaceGame(gameIdentity);
    this.initialiseGameServerConnection();
    this.initialiseGameRetryPolicySettings();
    this.initialiseGameServerService();
  }

  public dispose(): void {
    if (this.game && this.game.gameLoopHandler)  {
      this.game.gameLoopHandler.stopGameLoop();
    }
    super.dispose();
  }

  protected subscribeToGameEvents(): void {
    this.serverSubscriptionStore.push(
      this.gameServerService.subscribeCatchUp(this.handleCatchUpMessage.bind(this)),
      this.gameServerService.subscribeReset(this.handleResetMessage.bind(this)),
      this.gameServerService.subscribeNextGame(this.handleNextGameMessage.bind(this)),
      this.gameServerService.subscribePrizeWinner(this.handlePrizeWinnerMessage.bind(this)),
      this.gameServerService.subscribeHamsterRaceInfo(this.handleHamsterRaceInfoMessage.bind(this))
    );
  }

  protected handleResetMessage(payload: ResetData): void {
    const hamstersPayload = payload as HamsterRaceResetData;
    this.initialiseHamsterRaceGameLoop(hamstersPayload);

    this.changeGameState(GameState.InPlay);

    this.changeGameRepresentation({
      gameId: hamstersPayload.gameId,
      numberOfPlayers: hamstersPayload.numberOfPlayers,
      isLinkedGame: hamstersPayload.isLinkedGame,
      prizeStructure: hamstersPayload.prizeStructure,
      hamsterSet: hamstersPayload.hamsterSet
    });
  }

  protected handleCatchUpMessage(payload: CatchUpData): void {
    const hamstersPayload = payload as HamsterRaceCatchUpData;
    this.initialiseHamsterRaceGameLoop(hamstersPayload, hamstersPayload.stepsTaken);

    this.changeGameState(GameState.InPlay);

    this.changeGameRepresentation({
      gameId: hamstersPayload.gameNo,
      numberOfPlayers: hamstersPayload.numberOfPlayers,
      isLinkedGame: hamstersPayload.isLinkedGame,
      nextGame: null,
      isJackpotWon: null,
      prizeStructure: hamstersPayload.prizeStructure,
      winners: null,
      hamsterSet: hamstersPayload.hamsterSet
    });
    this.game.currentHamsterSet = hamstersPayload.hamsterSet;
  }

  private initialiseHamsterRaceGameLoop(payload: HamsterRaceResetData | HamsterRaceCatchUpData, initialStepsTaken: number = 0) {
    if (!this.game.gameLoopHandler) {
      this.game.gameLoopHandler = new HamsterRaceGameLoopHandler();
    }
    this.game.gameLoopHandler.initialise(payload.gameSequence);
    this.game.gameLoopHandler.startGameLoop(this.handleGameLoopCallback.bind(this), initialStepsTaken);
  }

  private handleGameLoopCallback(hamsterInFirstPlace: IHamster, allHamsters: IHamster[]) {
    this.changeGameRepresentation({
      hamsterInFirstPlace,
      hamsters: allHamsters
    });

    if (this.game.gameLoopHandler.isGameComplete()) {
      this.displatWinnersPanel();
    }
  }

  protected handlePrizeWinnerMessage(payload: PrizeWinnerData): void {
    this.prizeWinnersPayload = payload as HamsterRacePrizeWinner;
  }

  protected notifyGameWon() {
    this.onGameWonObservable.next({
      gameWinnerInfo: this.game.representation.winners,
      isJackpotWon: this.game.representation.isJackpotWon,
      numberOfPlayers: this.game.representation.numberOfPlayers,
      totalWinners: this.game.representation.totalWinners,
      totalPrizes: this.game.representation.totalPrizes,
      hamsterWinners: this.game.representation.hamsterWinners
    });
  }

  protected async handleHamsterRaceInfoMessage(payload: HamsterRaceInfoData): Promise<void> {
    const hamsterRaceTicketSetConfiguration: IHamsterRaceTicketSetConfigurationViewModel = await this.configurationService.getHamsterRaceTicketSetConfiguration();

    const currentHamsterSetNames = this.getCurrentHamsterSetNames(hamsterRaceTicketSetConfiguration);

    const lastEightWinnersNames = this.getLastEightWinnersNames(payload.lastEightWinners, currentHamsterSetNames);

    this.setHamsterStatsNames(currentHamsterSetNames, payload.hamsterStats);

    this.changeGameRepresentation({
      lastEightWinnersNames,
      hamsterStats: payload.hamsterStats
    });
  }

  private getInitialHamsterRaceGame(gameIdentity: GameIdentity): HamsterRaceGame {
    const gameConfig = this.getGameConfiguration(gameIdentity);
    const game = new HamsterRaceGame(gameIdentity, gameConfig);
    game.representation = {
      callboard: null
    };
    return game;
  }

  private getCurrentHamsterSetNames(hamsterRaceTicketSetConfiguration: IHamsterRaceTicketSetConfigurationViewModel) {
    return hamsterRaceTicketSetConfiguration.TicketSets
              .filter((x) => x.TicketSetId === this.game.currentHamsterSet)
              .sort((x, y) => Number(x.HamsterId) - Number(y.HamsterId))
              .map((x) => x.Name);
  }

  private getLastEightWinnersNames(lastEightWinners: number[], currentHamsterSetNames: string[]) {
    const lastEightWinnersNames: string[] = [];

    lastEightWinners.forEach((winner) => {
      lastEightWinnersNames.push(currentHamsterSetNames[winner - 1]);
    });

    return lastEightWinnersNames;
  }

  private setHamsterStatsNames(currentHamsterSetNames: string[], hamsterStats: IHamsterStats[]) {
    hamsterStats.forEach((hamster) => {
      hamster.hamsterName = currentHamsterSetNames[hamster.hamsterId - 1];
    });

    hamsterStats = hamsterStats.sort((x, y) => Number(y.racesWon) - Number(x.racesWon));
  }

  private getHamsterRaceWinnersFromPrizeWinnerData(payload: HamsterRacePrizeWinner) {
    const winners: IHamsterRaceWinner[] = [];

    if (payload && payload.winners) {
      payload.winners.forEach((winner) => {
        winners.push(new HamsterRaceWinner(winner.username, winner.prizeInPence));
      });
    }

    return winners;
  }

  protected changeGameRepresentation(newGameRepresentation: Partial<IHamsterRaceGameRepresentation>): void {
    this.game.updateRepresentation(newGameRepresentation);
    this.notifyGameRepresentationChanged();
  }

  private displatWinnersPanel() {
    const displayWinnersForInMilliseconds = 20000;
    const hamsterWinners = this.getHamsterRaceWinnersFromPrizeWinnerData(this.prizeWinnersPayload);

    this.changeGameRepresentation({
      hamsterWinners,
      numberOfPlayers: this.prizeWinnersPayload ? this.prizeWinnersPayload.totalPlayers : 0,
      totalWinners: this.prizeWinnersPayload ? this.prizeWinnersPayload.totalWinners : 0,
      totalPrizes: this.prizeWinnersPayload ? this.prizeWinnersPayload.totalPrizes : 0
    });

    this.changeGameState(GameState.WinnersDisplayed);
    this.notifyGameWon();
    this.trackRaceWinners(hamsterWinners);

    setTimeout(() => {
      this.changeGameState(GameState.Purchasing);
    }, displayWinnersForInMilliseconds);
  }

  private trackRaceWinners(hamsterWinners: IHamsterRaceWinner[]): void {
    const gameIdentity = this.game.gameIdentity;
    const trackedWinners = (hamsterWinners || []).map<ITrackedWinner>((hamsterWinner) => ({
      gameName: gameIdentity.name,
      clubName: gameIdentity.clubName,
      username: hamsterWinner.username,
      prizeInPence: hamsterWinner.prizeInPence
    }));
    this.winnersTrackerService.trackWinners(...trackedWinners);
  }
}

export { HamsterRaceGameController, IHamsterRaceGameRepresentation, IHamsterRaceWinner };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/hamster-race/_domain/controller/game-controller.ts